/**
 * AssetActivityTimeline — vertical timeline inside AssetDetailPanel.
 *
 * Shows recent activity entries for a specific asset.
 */

import { useEffect, useState } from 'react'
import { Upload, Edit3, Trash2, Cpu, Brain, Clock } from 'lucide-react'
import type { AssetActivity } from '../../types/fileManager'
import { assetApi } from '../../services/assetFolderApi'

const ACTIVITY_ICONS: Record<string, React.ReactNode> = {
  asset_added:    <Upload className="w-3.5 h-3.5 text-emerald-400" />,
  asset_modified: <Edit3 className="w-3.5 h-3.5 text-blue-400" />,
  asset_deleted:  <Trash2 className="w-3.5 h-3.5 text-red-400" />,
  ingest:         <Cpu className="w-3.5 h-3.5 text-amber-400" />,
  analysis:       <Brain className="w-3.5 h-3.5 text-purple-400" />,
}

function getIcon(type: string) {
  return ACTIVITY_ICONS[type] || <Clock className="w-3.5 h-3.5 text-[var(--rq-text-soft)]" />
}

function timeAgo(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diff = now - then
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

interface AssetActivityTimelineProps {
  assetId: number
}

export default function AssetActivityTimeline({ assetId }: AssetActivityTimelineProps) {
  const [activities, setActivities] = useState<AssetActivity[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    assetApi.getActivity(assetId, 50).then(data => {
      if (!cancelled) {
        setActivities(data.activities)
        setLoading(false)
      }
    }).catch(() => {
      if (!cancelled) setLoading(false)
    })
    return () => { cancelled = true }
  }, [assetId])

  if (loading) {
    return <div className="text-xs text-[var(--rq-text-soft)] py-4 text-center">Loading activity...</div>
  }

  if (activities.length === 0) {
    return <div className="text-xs text-[var(--rq-text-soft)] py-4 text-center">No activity yet</div>
  }

  return (
    <div className="space-y-0.5 max-h-60 overflow-y-auto">
      {activities.map((a, i) => (
        <div key={i} className="fm-timeline-item flex items-start gap-2.5 py-2 px-1">
          <div className="mt-0.5 flex-shrink-0">{getIcon(a.activity_type)}</div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-[var(--rq-text)] leading-snug truncate">{a.description}</p>
            <p className="text-[10px] text-[var(--rq-text-soft)] mt-0.5">{timeAgo(a.created_at)}</p>
          </div>
        </div>
      ))}
    </div>
  )
}
